/**
 * Admin summary API functions for operational visibility
 */

import { apiGet } from "./client.js";

export interface OrgSummary {
  id: string;
  name: string;
  country: string;
  plan: { id: string; name: string; priceEurCents: number } | null;
  subscriptionStatus: string | null;
  websitesUsed: number;
  websitesLimit: number;
  scansUsedThisMonth: number;
  scansLimitPerMonth: number;
  lastActivityAt: string | null;
  warnings: string[];
}

export interface WebsiteSummary {
  id: string;
  url: string;
  domain: string;
  orgId: string;
  verified: boolean;
  latestScore: number | null;
  latestConfidence: string | null;
  latestReportAt: string | null;
  lastSuccessfulRunAt: string | null;
  lastFailedRunAt: string | null;
  documentScanEnabled: boolean;
  recurringEnabled: boolean;
  warnings: string[];
}

export interface RunSummary {
  executionId: string;
  websiteId: string;
  websiteUrl: string;
  userId: string;
  status: string;
  stages: Array<{
    stage: string;
    runId: string;
    status: string;
    startedAt: string;
    completedAt: string | null;
  }>;
  startedAt: string;
  completedAt: string | null;
}

export interface DocumentSummary {
  documentRunId: string;
  websiteId: string;
  websiteUrl: string;
  documentsDetected: number;
  documentsScanned: number;
  documentsFailed: number;
  findingsCount: number;
  pacStatus: string | null;
  warnings: string[];
  completedAt: string | null;
}

export interface StatementSummary {
  id: string;
  websiteId: string;
  websiteUrl: string;
  versionCount: number;
  latestGeneratedAt: string;
  scoreValue: number | null;
  confidence: string | null;
  warnings: string[];
}

export interface RecurringSummary {
  runId: string;
  websiteId: string;
  websiteUrl: string;
  baselineScore: number | null;
  currentScore: number | null;
  scoreDelta: number | null;
  templatesNew: number;
  templatesRemoved: number;
  issueGroupsResolved: number;
  issueGroupsNew: number;
  rerundecision: string | null;
  completedAt: string | null;
}

export interface SupportAlert {
  type:
    | "low_confidence"
    | "failed_run"
    | "waf_blocking"
    | "manual_only"
    | "pac_unavailable";
  websiteId: string;
  websiteUrl: string;
  message: string;
  severity: "warning" | "critical";
}

export async function listOrgs(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{ orgs: OrgSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.search) qs.set("search", params.search);
  const q = qs.toString();
  return apiGet(`/admin/orgs${q ? `?${q}` : ""}`);
}

export async function getOrg(orgId: string): Promise<OrgSummary> {
  return apiGet(`/admin/orgs/${orgId}`);
}

export async function listWebsites(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{ websites: WebsiteSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.search) qs.set("search", params.search);
  const q = qs.toString();
  return apiGet(`/admin/websites${q ? `?${q}` : ""}`);
}

export async function getWebsite(websiteId: string): Promise<WebsiteSummary> {
  return apiGet(`/admin/websites/${websiteId}`);
}

export async function listRuns(params?: {
  page?: number;
  limit?: number;
}): Promise<{ runs: RunSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiGet(`/admin/runs${q ? `?${q}` : ""}`);
}

export async function listDocuments(params?: {
  page?: number;
  limit?: number;
}): Promise<{ documents: DocumentSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiGet(`/admin/documents${q ? `?${q}` : ""}`);
}

export async function listStatements(params?: {
  page?: number;
  limit?: number;
}): Promise<{ statements: StatementSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiGet(`/admin/statements${q ? `?${q}` : ""}`);
}

export async function listRecurring(params?: {
  page?: number;
  limit?: number;
}): Promise<{ recurring: RecurringSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiGet(`/admin/recurring${q ? `?${q}` : ""}`);
}

export async function getSupportAlerts(): Promise<{ alerts: SupportAlert[] }> {
  return apiGet(`/admin/support`);
}
